"use client";

import type { ComponentType, SVGProps } from "react";
import type { LeadFlowAnswers, LeadFlowChoiceArrangement } from "../../types";

import CareRecipientParents from "@homeinstead/icons/CareRecipientParents";
import CareRecipientPerson from "@homeinstead/icons/CareRecipientPerson";
import CareRecipientSpouse from "@homeinstead/icons/CareRecipientSpouse";
import { cn } from "@homeinstead/ui";
import { useEffect, useId, useRef } from "react";
import { CHOICE_ARRANGEMENT_CLASSES } from "../../constants/choice-arrangement-classes";
import { useAnswer } from "../../hooks/use-answers";
import { useDefaultAnswer } from "../../hooks/use-default-answer";
import { isChoiceValue } from "../../utils/is-choice-value";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";
import { useLeadFlowStep } from "../LeadFlowStepContext";
import { ChoiceOption } from "./ChoiceOption";
import { FieldError } from "./FieldError";
import { QuestionLegend } from "./QuestionLegend";

// Matches the check badge's enter and draw animations in the shared
// Tailwind stylesheet; auto-advance waits for them to finish.
const SELECTION_FEEDBACK_MILLISECONDS = 180;

type SingleChoiceFieldName = "careFor" | "urgency";

type ChoiceIcon = ComponentType<SVGProps<SVGSVGElement>>;

/** Built-in icons by answer value, used when an answer has no authored icon. */
const BUILT_IN_ICONS: Partial<
  Record<SingleChoiceFieldName, Record<string, ChoiceIcon>>
> = {
  careFor: {
    me: CareRecipientPerson,
    other: CareRecipientPerson,
    parent: CareRecipientParents,
    spouse: CareRecipientSpouse,
  },
};

type ChoiceOption = {
  _key: string;
  description?: string | null;
  iconUrl?: string | null;
  label?: string | null;
  value?: string | null;
};

type SingleChoiceQuestionProps = {
  fieldName: SingleChoiceFieldName;
  item: {
    arrangement?: LeadFlowChoiceArrangement | null;
    autoAdvance?: boolean | null;
    defaultAnswer?: string | null;
    helperText?: string | null;
    label?: string | null;
    options?: ChoiceOption[] | null;
  };
};

function SingleChoiceQuestion({ fieldName, item }: SingleChoiceQuestionProps) {
  const [value, setValue] = useAnswer(fieldName);
  const { advance, errors, isInert = false } = useLeadFlowStep();
  const { isSectionTakeover = false } = useLeadFlowRuntime();
  const errorId = useId();
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const arrangement = item.arrangement ?? "list";

  useDefaultAnswer(
    fieldName,
    isChoiceValue(fieldName, item.defaultAnswer)
      ? item.defaultAnswer
      : undefined,
  );

  useEffect(() => () => clearTimeout(advanceTimeoutRef.current), []);

  const handleSelect = (
    nextValue: NonNullable<LeadFlowAnswers[SingleChoiceFieldName]>,
  ) => {
    if (isInert) {
      return;
    }

    setValue(nextValue);

    if (!item.autoAdvance) {
      return;
    }

    // A pending advance is replaced, never stacked, so one interaction moves
    // at most one step.
    clearTimeout(advanceTimeoutRef.current);
    advanceTimeoutRef.current = setTimeout(
      advance,
      SELECTION_FEEDBACK_MILLISECONDS,
    );
  };

  const fieldErrors = errors[fieldName];

  return (
    <fieldset
      aria-describedby={fieldErrors?.length ? errorId : undefined}
      className="flex w-full min-w-0 flex-col border-none p-0"
    >
      <QuestionLegend helperText={item.helperText} label={item.label} />
      <div
        className={cn(
          CHOICE_ARRANGEMENT_CLASSES[arrangement],
          // Takeover pills sit closer together below `md`.
          isSectionTakeover && "max-md:gap-3",
        )}
      >
        {(item.options ?? []).flatMap(({ value: optionValue, ...option }) =>
          option.label && isChoiceValue(fieldName, optionValue)
            ? [
                <ChoiceOption
                  arrangement={arrangement}
                  builtInIcon={BUILT_IN_ICONS[fieldName]?.[optionValue]}
                  description={option.description}
                  iconUrl={option.iconUrl}
                  isSelected={value === optionValue}
                  key={option._key}
                  label={option.label}
                  name={fieldName}
                  onSelect={() => handleSelect(optionValue)}
                  type="radio"
                  value={optionValue}
                />,
              ]
            : [],
        )}
      </div>
      <FieldError id={errorId} messages={fieldErrors} />
    </fieldset>
  );
}

export { SingleChoiceQuestion };
